import { combineLatest, merge, MonoTypeOperatorFunction, Observable, of, OperatorFunction } from 'rxjs';
import { debounceTime, filter, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';
import { isNotNil } from './is-not-nil';

export function isNotNullOrUndefined<T>(): OperatorFunction<null | undefined | T, T> {
    return filter(isNotNil);
}

export function isNotNull<T>(): OperatorFunction<null | T, T> {
    return filter((value): value is T => value !== null);
}

export function isNotUndefined<T>(): OperatorFunction<undefined | T, T> {
    return filter((value): value is T => value !== undefined);
}

// In almost all cases you want to use this one
export function cacheValueWhileSubscribed<T>(bufferSize: number = 1): MonoTypeOperatorFunction<T> {
    return shareReplay<T>({ bufferSize, refCount: true });
}

// WILL cause memory leaks if not manually cleaned up. Only to be used in singleton services.
export function cacheValueForever<T>(bufferSize: number = 1): MonoTypeOperatorFunction<T> {
    return shareReplay<T>({ bufferSize, refCount: false });
}

export function combineWith<T, S extends any>(...sources$: Observable<S>[]): OperatorFunction<T, [T, ...S[]]> {
    return (source$: Observable<T>) =>
        source$.pipe(switchMap(source => combineLatest([of(source), ...sources$]) as Observable<[T, ...S[]]>));
}

export function debugStream<T>(tag: string | number = ''): MonoTypeOperatorFunction<T> {
    return tap(
        next => {
            console.log(tag, '::', next);
        },
        error => {
            console.error(tag, '::', error);
        },
        () => {
            console.log(tag, '::', 'stream complete');
        }
    );
}

export function debugStreamDelayed<T>(tag: string = '', delay = 1000): MonoTypeOperatorFunction<T> {
    const doPrint: (message: any) => void = message =>
        setTimeout(() => {
            console.log(tag, '::', JSON.stringify(message));
        }, delay);
    return tap(
        next => {
            doPrint(next);
        },
        error => {
            doPrint(error);
        },
        () => {
            doPrint('stream complete');
        }
    );
}

export function switchMapWithUpstream<T, O>(
    stream$: Observable<O>
): (source: Observable<T>) => Observable<readonly [T, O]> {
    return (source$: Observable<T>) =>
        source$.pipe(switchMap(val => stream$.pipe(map(streamVal => [val, streamVal] as const))));
}

export function throwIfUndefined<T>(
    errorMessage: string = 'stream value should not be undefined'
): (source: Observable<undefined | T>) => Observable<T> {
    return (source: Observable<undefined | T>) =>
        source.pipe(
            map(value => {
                if (typeof value === 'undefined') {
                    throw new Error(errorMessage);
                } else {
                    return value;
                }
            })
        );
}

export function throwIfNull<T>(
    errorMessage: string = 'stream value should not be null'
): (source: Observable<null | T>) => Observable<T> {
    return (source: Observable<null | T>) =>
        source.pipe(
            map(value => {
                if (value === null) {
                    throw new Error(errorMessage);
                } else {
                    return value;
                }
            })
        );
}

export function throwIfNullOrUndefined<T>(
    errorMessage: string = 'stream value should not be null or undefined'
): (source: Observable<null | undefined | T>) => Observable<T> {
    return (source: Observable<undefined | null | T>) =>
        source.pipe(
            map(value => {
                if (typeof value === 'undefined' || value === null) {
                    throw new Error(errorMessage);
                } else {
                    return value;
                }
            })
        );
}

export function throwIfFalsy<T>(
    errorMessage: string = 'stream value should not be null or undefined'
): (source: Observable<null | undefined | false | '' | 0 | T>) => Observable<T> {
    return (source: Observable<null | undefined | false | '' | 0 | T>) =>
        source.pipe(
            map(value => {
                if (!value) {
                    throw new Error(errorMessage);
                } else {
                    return value;
                }
            })
        );
}

// Will perform side-effect on emit, error and complete
export function tapAlways<T>(always: () => void): MonoTypeOperatorFunction<T> {
    return tap(
        () => always(),
        () => always(),
        () => always()
    );
}

/**
 * Returns an observable that emits the first and last events in a window of time. Other events in this window are dropped.
 * Useful to get immediate results when few events are emitted or to suppress events when a lot of events are emitted.
 * Functions the same as debounceTime, but emits immediately before waiting.
 * Input:
 * A---B---C-----------D---E
 * Emits:
 * A----------C-----------D-----------E
 * @param duration Duration of the window in ms
 */
export function leadingDebounceTime<T>(duration: number): OperatorFunction<T, T> {
    return (source$: Observable<T>): Observable<T> => {
        let leadingEmitted: boolean = false;
        let burstCounter: number = 0;
        let leadingTag: number = 0;

        const shared$: Observable<{ value: T; tag: number }> = source$.pipe(
            map(value => ({ value, tag: burstCounter })),
            tap(() => burstCounter++),
            share()
        );

        // Emits the last value of a burst
        const debouncedStream$: Observable<T> = shared$.pipe(
            debounceTime(duration),
            map(value => ({ value, isDuplicate: value.tag === leadingTag })),
            tap(() => {
                leadingEmitted = false;
                burstCounter = 0;
            }),
            filter(({ isDuplicate }) => !isDuplicate), // Make sure we don't emit the initial event again
            map(({ value }) => value.value)
        );

        // Emits the initial value of a burst
        const leadingStream$: Observable<T> = shared$.pipe(
            filter(() => !leadingEmitted),
            tap(value => {
                leadingEmitted = true;
                leadingTag = value.tag;
            }),
            map(value => value.value)
        );
        return merge(leadingStream$, debouncedStream$);
    };
}
